/*
 * Iter 31.306 (D2) — Content Blocks admin panel.
 *
 * Lists every content block currently persisted in `db.content_blocks`
 * and lets One edit any body inline. Also supports creating brand-new
 * keys — paste a dotted namespace (e.g. `programme.spy_radio.about`)
 * and the value into the "new block" form. Delete is available for
 * rollback (frontend hook falls through to hardcoded fallback).
 *
 * This is the "admin edits, it's live, Three isn't contacted" surface
 * from Two's D2 brief. Every hard-coded string across the app can
 * migrate here at One's pace via useContentBlock.
 */
import React, { useCallback, useEffect, useReducer } from "react";
import { api } from "@/lib/api";
import { invalidateContentBlockCache } from "@/lib/useContentBlock";
import {
    Loader2, AlertTriangle, Plus, Edit3, Save, Trash2,
    CheckCircle2, FileText,
} from "lucide-react";

const INITIAL = {
    blocks: [], loading: true, err: null,
    editKey: null, editBody: "", editDesc: "",
    saving: false, saveErr: null, savedKey: null,
    newKey: "", newBody: "", newDesc: "",
    creating: false, createErr: null,
    busyKey: null,
};

function reducer(s, a) {
    switch (a.type) {
        case "load_ok": return { ...s, blocks: a.blocks, loading: false, err: null };
        case "load_err": return { ...s, loading: false, err: a.err };
        case "edit_start": return { ...s, editKey: a.block.key, editBody: a.block.body_md || "", editDesc: a.block.description || "", saveErr: null, savedKey: null };
        case "edit_cancel": return { ...s, editKey: null, editBody: "", editDesc: "", saveErr: null };
        case "edit_body": return { ...s, editBody: a.v };
        case "edit_desc": return { ...s, editDesc: a.v };
        case "save_busy": return { ...s, saving: true, saveErr: null };
        case "save_err": return { ...s, saving: false, saveErr: a.err };
        case "save_ok": return { ...s, saving: false, saveErr: null, savedKey: a.key, editKey: null };
        case "new_key": return { ...s, newKey: a.v.toLowerCase().replace(/[^a-z0-9._-]/g, "") };
        case "new_body": return { ...s, newBody: a.v };
        case "new_desc": return { ...s, newDesc: a.v };
        case "create_busy": return { ...s, creating: true, createErr: null };
        case "create_err": return { ...s, creating: false, createErr: a.err };
        case "create_ok": return { ...s, creating: false, createErr: null, newKey: "", newBody: "", newDesc: "" };
        case "busy": return { ...s, busyKey: a.key };
        case "busy_done": return { ...s, busyKey: null };
        default: return s;
    }
}

export default function ContentBlocksPanel() {
    const [s, dispatch] = useReducer(reducer, INITIAL);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/content/blocks");
            dispatch({ type: "load_ok", blocks: data?.blocks || [] });
        } catch (e) {
            dispatch({
                type: "load_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const save = useCallback(async () => {
        if (!s.editKey) return;
        dispatch({ type: "save_busy" });
        try {
            await api.patch(
                `/admin/content/blocks/${encodeURIComponent(s.editKey)}`,
                { body_md: s.editBody, description: s.editDesc || undefined },
            );
            dispatch({ type: "save_ok", key: s.editKey });
            invalidateContentBlockCache();
            reload();
        } catch (e) {
            dispatch({
                type: "save_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, [s.editKey, s.editBody, s.editDesc, reload]);

    const create = useCallback(async () => {
        if (!s.newKey || !s.newBody) return;
        dispatch({ type: "create_busy" });
        try {
            await api.patch(
                `/admin/content/blocks/${encodeURIComponent(s.newKey)}`,
                { body_md: s.newBody, description: s.newDesc || undefined },
            );
            dispatch({ type: "create_ok" });
            invalidateContentBlockCache();
            reload();
        } catch (e) {
            dispatch({
                type: "create_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, [s.newKey, s.newBody, s.newDesc, reload]);

    const remove = useCallback(async (key) => {
        if (!window.confirm(
            `Delete content block "${key}"?\n\n` +
            "Any component using useContentBlock('" + key + "') will " +
            "fall through to its hardcoded fallback."
        )) return;
        dispatch({ type: "busy", key });
        try {
            await api.delete(`/admin/content/blocks/${encodeURIComponent(key)}`);
            invalidateContentBlockCache();
            reload();
        } catch (e) {
            window.alert(`Delete failed: ${e.response?.data?.detail || e.message}`);
        } finally { dispatch({ type: "busy_done" }); }
    }, [reload]);

    return (
        <div
            className="paper-card border-2 border-ink/30 p-5 mb-6"
            data-testid="content-blocks-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <FileText className="w-4 h-4 text-stamp-red" />
                    Content Blocks
                </h2>
                <button
                    type="button"
                    onClick={reload}
                    disabled={s.loading}
                    data-testid="cb-refresh"
                    className="font-heading uppercase tracking-[0.16em] text-[10px] text-ink-soft hover:text-stamp-red disabled:opacity-50"
                >
                    {s.loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "refresh"}
                </button>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Iter 31.306 (D2) — every string on the app that has been wrapped with{" "}
                <code className="font-mono">useContentBlock(&quot;key&quot;, fallback)</code>{" "}
                is editable here. Save takes effect within 5 minutes on production; force-refresh a page in the meantime to see updates immediately. Delete = revert to code fallback.
            </div>

            {s.err ? (
                <div className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1" data-testid="cb-load-err">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{s.err}</span>
                </div>
            ) : null}

            {/* Existing blocks */}
            <div className="border border-ink/20 mb-4 divide-y divide-ink/15" data-testid="cb-list">
                {s.loading && s.blocks.length === 0 ? (
                    <div className="p-3 font-body text-[11px] text-ink-faint italic" data-testid="cb-loading">Loading…</div>
                ) : s.blocks.length === 0 ? (
                    <div className="p-3 font-body text-[11px] text-ink-faint italic" data-testid="cb-empty">
                        No content blocks yet. Use the form below to create one, then wrap a hard-coded string in the app with{" "}
                        <code className="font-mono">useContentBlock</code>.
                    </div>
                ) : (
                    s.blocks.map((b) => (
                        <div key={b.key} className="p-3" data-testid={`cb-block-${b.key}`}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <code className="font-mono text-[11px] text-ink font-bold">{b.key}</code>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => dispatch({ type: "edit_start", block: b })}
                                        data-testid={`cb-edit-${b.key}`}
                                        className="font-heading uppercase text-[9px] tracking-[0.18em] px-2 py-1 border border-ink/30 text-ink-soft hover:text-stamp-red hover:border-stamp-red inline-flex items-center gap-1"
                                    >
                                        <Edit3 className="w-3 h-3" />
                                        edit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => remove(b.key)}
                                        disabled={s.busyKey === b.key}
                                        data-testid={`cb-delete-${b.key}`}
                                        className="font-heading uppercase text-[9px] tracking-[0.18em] px-2 py-1 border border-ink/30 text-ink-soft hover:text-stamp-red hover:border-stamp-red disabled:opacity-50 inline-flex items-center gap-1"
                                    >
                                        {s.busyKey === b.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                        delete
                                    </button>
                                </div>
                            </div>
                            {s.savedKey === b.key ? (
                                <div className="mb-2 text-[10px] text-emerald-700 font-body flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" />
                                    saved
                                </div>
                            ) : null}
                            {s.editKey === b.key ? (
                                <div className="mt-2 space-y-2" data-testid={`cb-editor-${b.key}`}>
                                    <textarea
                                        value={s.editBody}
                                        onChange={(e) => dispatch({ type: "edit_body", v: e.target.value })}
                                        rows={5}
                                        data-testid={`cb-editor-body-${b.key}`}
                                        className="w-full px-2 py-1.5 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink leading-relaxed"
                                    />
                                    <input
                                        type="text"
                                        value={s.editDesc}
                                        placeholder="Description (optional, for editors only)"
                                        onChange={(e) => dispatch({ type: "edit_desc", v: e.target.value })}
                                        data-testid={`cb-editor-desc-${b.key}`}
                                        className="w-full px-2 py-1.5 border border-ink/30 bg-paper-light font-mono text-[10px] text-ink"
                                    />
                                    {s.saveErr ? (
                                        <div className="text-[11px] text-stamp-red font-body" data-testid="cb-save-err">
                                            {s.saveErr}
                                        </div>
                                    ) : null}
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={save}
                                            disabled={s.saving}
                                            data-testid={`cb-save-${b.key}`}
                                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-50 inline-flex items-center gap-1.5"
                                        >
                                            {s.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                            Save
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => dispatch({ type: "edit_cancel" })}
                                            disabled={s.saving}
                                            data-testid={`cb-cancel-${b.key}`}
                                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/30 text-ink-soft hover:text-stamp-red"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="font-body text-[11px] text-ink-soft whitespace-pre-wrap leading-relaxed pl-1">
                                        {b.body_md?.length > 220 ? b.body_md.slice(0, 220) + "…" : (b.body_md || "")}
                                    </div>
                                    {b.description ? (
                                        <div className="mt-1 font-body text-[10px] text-ink-faint italic pl-1">
                                            note: {b.description}
                                        </div>
                                    ) : null}
                                    {b.updated_at ? (
                                        <div className="mt-1 font-body text-[10px] text-ink-faint pl-1">
                                            updated {b.updated_at}
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Create new block */}
            <div className="border border-ink/20 p-3" data-testid="cb-create-form">
                <div className="label-typed text-[10px] text-ink mb-2 flex items-center gap-2">
                    <Plus className="w-3 h-3" />
                    Create new content block
                </div>
                <div className="space-y-2 mb-2">
                    <input
                        type="text"
                        placeholder="key · dotted.namespace (e.g. home.tagline)"
                        value={s.newKey}
                        onChange={(e) => dispatch({ type: "new_key", v: e.target.value })}
                        data-testid="cb-new-key"
                        className="w-full px-2 py-1.5 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink"
                        maxLength={80}
                    />
                    <textarea
                        value={s.newBody}
                        onChange={(e) => dispatch({ type: "new_body", v: e.target.value })}
                        placeholder="Body (markdown supported downstream if the consumer renders it)"
                        rows={3}
                        data-testid="cb-new-body"
                        className="w-full px-2 py-1.5 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink"
                    />
                    <input
                        type="text"
                        value={s.newDesc}
                        onChange={(e) => dispatch({ type: "new_desc", v: e.target.value })}
                        placeholder="Description (optional, for editors)"
                        data-testid="cb-new-desc"
                        className="w-full px-2 py-1.5 border border-ink/30 bg-paper-light font-mono text-[10px] text-ink"
                    />
                </div>
                {s.createErr ? (
                    <div className="text-[11px] text-stamp-red font-body mb-2" data-testid="cb-create-err">
                        {s.createErr}
                    </div>
                ) : null}
                <button
                    type="button"
                    onClick={create}
                    disabled={s.creating || !s.newKey || !s.newBody}
                    data-testid="cb-create-submit"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-40 inline-flex items-center gap-1.5"
                >
                    {s.creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Create block
                </button>
            </div>
        </div>
    );
}
